import { useCallback, useEffect, useState } from 'react';

import {
  listUsers, userCounts, setSuspension, approveInvestor, type AdminUser,
} from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { ROLES, type Role } from '../../auth/types';
import { formatDateShort, shortId } from '../../lib/format';
import './AdminPages.css';

/**
 * User management across all nine roles.
 *
 * The investor double gate gets its own treatment on purpose. Holding the investor
 * role grants nothing: the engine requires investor_accounts.approved_by_admin as
 * a separate condition, and approving also provisions a Wefunder investor
 * record. Presenting that as another status toggle would misrepresent both the
 * authority being granted and the side effect.
 */
export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [counts, setCounts] = useState<{ role: Role; total: number; suspended: number }[]>([]);
  const [role, setRole] = useState<string>('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listUsers({ role: role || undefined, search: query || undefined })
      .then(setUsers)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load users.');
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, [role, query]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { userCounts().then(setCounts).catch(() => setCounts([])); }, []);

  async function toggleSuspension(user: AdminUser) {
    const next = !user.suspended;
    let reason: string | undefined;
    if (next) {
      const entered = window.prompt(
        `Suspend ${user.email}?\n\nThis blocks them from signing in. Reason (recorded in the audit log):`,
      );
      if (entered === null) return;
      reason = entered.trim() || undefined;
    } else if (!window.confirm(`Reinstate ${user.email}? They will be able to sign in again.`)) {
      return;
    }

    setBusyId(user.userId);
    setError(null);
    try {
      const updated = await setSuspension(user.userId, next, reason);
      setUsers((cur) => cur?.map((u) => (u.userId === user.userId ? { ...u, ...updated } : u)) ?? cur);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not update that account.');
    } finally {
      setBusyId(null);
    }
  }

  async function grantInvestorApproval(user: AdminUser) {
    const ok = window.confirm(
      `Approve ${user.email} as an investor?\n\n`
      + 'This is the second half of the investor double gate: it grants access to '
      + 'investment opportunities and provisions a Wefunder investor record. '
      + 'It is not reversible from this screen.',
    );
    if (!ok) return;

    setBusyId(user.userId);
    setError(null);
    try {
      await approveInvestor(user.userId);
      setUsers((cur) =>
        cur?.map((u) => (u.userId === user.userId ? { ...u, investorApproved: true } : u)) ?? cur,
      );
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not approve that investor.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Users</h1>
          <p className="adm-lede">Every account across all nine roles.</p>
        </div>
      </header>

      {counts.length > 0 && (
        <div className="adm-counts">
          {counts.map((c) => (
            <button
              key={c.role}
              type="button"
              className={`adm-count${role === c.role ? ' is-active' : ''}`}
              onClick={() => setRole(role === c.role ? '' : c.role)}
            >
              <span className="adm-count__value mono">{c.total}</span>
              <span className="adm-count__label">{c.role}</span>
              {c.suspended > 0 && (
                <span className="adm-count__sub mono">{c.suspended} suspended</span>
              )}
            </button>
          ))}
        </div>
      )}

      <form
        className="adm-filters"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search.trim());
        }}
      >
        <input
          className="field__input adm-search"
          type="search"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="adm-select" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <button type="submit" className="button button--secondary">Search</button>
        {(query || role) && (
          <button
            type="button"
            className="button button--ghost"
            onClick={() => { setSearch(''); setQuery(''); setRole(''); }}
          >
            Clear
          </button>
        )}
      </form>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {loading && <p className="adm-status">Loading…</p>}
      {!loading && users?.length === 0 && <p className="adm-status adm-muted">No users match.</p>}

      {!loading && users && users.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Role</th>
                <th scope="col">Gate</th>
                <th scope="col">Status</th>
                <th scope="col">Joined</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.userId} className={u.suspended ? 'adm-row--suspended' : undefined}>
                  <td>
                    <div className="adm-user">
                      <span className="adm-user__name">{u.name || '-'}</span>
                      <span className="adm-user__email mono">{u.email}</span>
                      <span className="adm-user__id mono adm-muted" title={u.userId}>
                        {shortId(u.userId)}
                      </span>
                    </div>
                  </td>
                  <td><span className="adm-pill">{u.role}</span></td>
                  <td>
                    {u.investorApproved === true && (
                      <span className="adm-pill adm-pill--approved">Investor approved</span>
                    )}
                    {u.investorApproved === false && (
                      <span className="adm-pill adm-pill--pending">Awaiting approval</span>
                    )}
                    {u.artistApproved === true && (
                      <span className="adm-pill adm-pill--approved">Artist approved</span>
                    )}
                    {u.artistApproved === false && (
                      <span className="adm-pill adm-pill--pending">Artist pending</span>
                    )}
                    {u.investorApproved === null && u.artistApproved === null && (
                      <span className="adm-muted"> | </span>
                    )}
                  </td>
                  <td>
                    {u.suspended ? (
                      <span className="adm-pill adm-pill--suspended" title={u.suspensionReason ?? ''}>
                        Suspended
                      </span>
                    ) : (
                      <span className="adm-pill adm-pill--active">Active</span>
                    )}
                    {!u.verified && <span className="adm-pill adm-pill--pending">Unverified</span>}
                  </td>
                  <td className="mono adm-muted">{formatDateShort(u.createdAt)}</td>
                  <td className="adm-actions">
                    {/* The investor gate is a distinct, deliberate grant, not a toggle. */}
                    {u.investorApproved === false && (
                      <button
                        type="button"
                        className="button button--primary adm-btn-sm"
                        disabled={busyId === u.userId}
                        onClick={() => grantInvestorApproval(u)}
                      >
                        Approve investor
                      </button>
                    )}
                    <button
                      type="button"
                      className={`button adm-btn-sm ${u.suspended ? 'button--secondary' : 'button--ghost'}`}
                      disabled={busyId === u.userId}
                      onClick={() => toggleSuspension(u)}
                    >
                      {busyId === u.userId ? '…' : u.suspended ? 'Reinstate' : 'Suspend'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
